<template>
  <LoaderSpinner v-if="flagLoading" />
  <div
    v-else
    class="w-full h-auto p-5 bg-white rounded-xl border border-slate-200 flex flex-col gap-4"
  >
    <div class="flex gap-4" :class="isMobileView ? 'flex-col' : 'flex-row'">
      <div
        v-for="chart in charts"
        :key="chart.key"
        class="bg-white rounded-lg shadow dark:bg-gray-800 p-4 md:p-6"
        :class="isMobileView ? 'w-full' : 'w-1/2'"
      >
        <div class="self-stretch flex justify-between items-center mb-4">
          <div
            class="text-neutral-900 text-lg font-semibold font-['Public Sans'] leading-7"
          >
            {{ chart.title }}
          </div>
          <div
            class="text-slate-600 text-sm font-normal font-['Public Sans'] leading-snug"
          >
            {{ chart.total }} open
          </div>
        </div>
        <apexchart
          type="bar"
          height="300"
          :options="chart.options"
          :series="chart.series"
        ></apexchart>
      </div>
    </div>

    <!-- Línea de tiempo: día exacto de vencimiento por proyecto -->
    <div class="w-full bg-white rounded-lg shadow dark:bg-gray-800 p-4 md:p-6">
      <div class="self-stretch flex justify-between items-center mb-4">
        <div
          class="text-neutral-900 text-lg font-semibold font-['Public Sans'] leading-7"
        >
          Project Due Dates
        </div>
        <div
          class="flex items-center gap-4 text-slate-600 text-sm font-normal font-['Public Sans'] leading-snug"
        >
          <span class="flex items-center gap-1">
            <span class="w-3 h-3 rounded-sm" :style="{ background: OVERDUE_COLOR }"></span>
            Overdue ({{ overdueCount }})
          </span>
          <span class="flex items-center gap-1">
            <span class="w-3 h-3 rounded-sm" :style="{ background: DEFAULT_COLOR }"></span>
            Upcoming ({{ projectTimeline.length - overdueCount }})
          </span>
        </div>
      </div>
      <div
        v-if="projectTimeline.length === 0"
        class="text-slate-600 text-sm font-normal"
      >
        There are no open projects with a due date
      </div>
      <div v-else class="max-h-[520px] overflow-y-auto">
        <apexchart
          type="rangeBar"
          :height="timelineHeight"
          :options="timelineOptions"
          :series="timelineSeries"
        ></apexchart>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import ApexCharts from "vue3-apexcharts";
import LoaderSpinner from "@/components/CircularLoader.vue";

const OVERDUE_COLOR = "#DC2626";
const DEFAULT_COLOR = "#00225E";
const BUCKET_COLORS = {
  Overdue: OVERDUE_COLOR,
  "No due date": "#94A3B8",
};
const MAX_TOOLTIP_PROJECTS = 10;
const DAY_MS = 24 * 60 * 60 * 1000;

const escapeHtml = (text) =>
  String(text).replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]
  );

// "YYYY-MM-DD" -> medianoche local de ese día
const toLocalDay = (ymd) => {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const formatDay = (ymd) =>
  toLocalDay(ymd).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const describeDaysLeft = (days) => {
  if (days === 0) return "Due today";
  if (days < 0) return `${-days} day${days === -1 ? "" : "s"} overdue`;
  return `Due in ${days} day${days === 1 ? "" : "s"}`;
};

// Tooltip con los proyectos de la barra; showCount agrega "(n)" para tareas
const buildTooltip = (bucket, groups, total, unit, showCount) => {
  const shown = groups.slice(0, MAX_TOOLTIP_PROJECTS);
  const rest = groups.length - shown.length;
  const rows = shown
    .map(
      (g) =>
        `<li>${escapeHtml(g.name)}${showCount ? ` <span style="color:#64748B">(${g.count})</span>` : ""}</li>`
    )
    .join("");
  return `
    <div style="padding:8px 12px;max-width:280px;font-size:12px">
      <div style="font-weight:600;margin-bottom:4px">${bucket} · ${total} ${unit}</div>
      <ul style="margin:0;padding-left:16px;list-style:disc">${rows}</ul>
      ${rest > 0 ? `<div style="color:#64748B;margin-top:4px">+${rest} more</div>` : ""}
    </div>`;
};

export default {
  components: {
    apexchart: ApexCharts,
    LoaderSpinner,
  },
  setup() {
    const store = useStore();
    const isMobileView = computed(() => store.getters.isMobileView);
    const flagLoading = ref(true);

    const buckets = ref([]);
    const projectCounts = ref({});
    const taskCounts = ref({});
    const projectTimeline = ref([]);

    const toChart = (key, title, grouped, unit, showCount) => {
      const groupsByBucket = buckets.value.map((b) => grouped[b] || []);
      const data = groupsByBucket.map((groups) =>
        groups.reduce((sum, g) => sum + g.count, 0)
      );
      return {
        key,
        title,
        total: data.reduce((sum, n) => sum + n, 0),
        series: [{ name: title, data }],
        options: chartOptions(({ dataPointIndex }) =>
          buildTooltip(
            buckets.value[dataPointIndex],
            groupsByBucket[dataPointIndex],
            data[dataPointIndex],
            unit,
            showCount
          )
        ),
      };
    };

    const charts = computed(() => [
      toChart("projects", "Projects by Due Date", projectCounts.value, "projects", false),
      toChart("tasks", "Tasks by Due Date", taskCounts.value, "tasks", true),
    ]);

    const chartOptions = (tooltipContent) => ({
      chart: {
        type: "bar",
        toolbar: { show: false },
      },
      plotOptions: {
        bar: {
          distributed: true,
          borderRadius: 4,
          borderRadiusApplication: "end",
          columnWidth: "55%",
        },
      },
      colors: buckets.value.map((b) => BUCKET_COLORS[b] || DEFAULT_COLOR),
      dataLabels: { enabled: false },
      legend: { show: false },
      xaxis: { categories: buckets.value },
      yaxis: {
        forceNiceScale: true,
        labels: { formatter: (val) => Math.round(val) },
      },
      grid: { borderColor: "#E2E8F0" },
      tooltip: { custom: tooltipContent },
    });

    // Cada barra parte de hoy: los vencidos se extienden hacia atrás hasta su
    // fecha de vencimiento y los próximos hacia adelante hasta el final de ese día
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const overdueCount = computed(
      () => projectTimeline.value.filter((p) => p.days_left < 0).length
    );

    const timelineSeries = computed(() => [
      {
        name: "Projects",
        data: projectTimeline.value.map((p) => {
          const due = toLocalDay(p.due_date).getTime();
          const overdue = p.days_left < 0;
          return {
            x: `${p.name} · ${formatDay(p.due_date)}`,
            y: overdue ? [due, today.getTime()] : [today.getTime(), due + DAY_MS],
            fillColor: overdue ? OVERDUE_COLOR : DEFAULT_COLOR,
          };
        }),
      },
    ]);

    const timelineHeight = computed(() =>
      Math.max(220, projectTimeline.value.length * 34 + 80)
    );

    const timelineOptions = computed(() => ({
      chart: {
        type: "rangeBar",
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: true,
          borderRadius: 4,
          barHeight: "60%",
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (val, { dataPointIndex }) => {
          const days = projectTimeline.value[dataPointIndex].days_left;
          if (days === 0) return "Today";
          return days < 0 ? `${-days}d late` : `${days}d`;
        },
        style: { colors: ["#FFFFFF"], fontSize: "11px" },
      },
      xaxis: {
        type: "datetime",
        labels: { datetimeUTC: false, format: "MMM dd" },
      },
      yaxis: {
        labels: { maxWidth: 260 },
      },
      grid: {
        borderColor: "#E2E8F0",
        xaxis: { lines: { show: true } },
      },
      annotations: {
        xaxis: [
          {
            x: today.getTime(),
            borderColor: "#0F172A",
            strokeDashArray: 4,
            label: {
              text: "Today",
              orientation: "horizontal",
              borderColor: "#0F172A",
              style: { color: "#FFFFFF", background: "#0F172A" },
            },
          },
        ],
      },
      tooltip: {
        custom: ({ dataPointIndex }) => {
          const p = projectTimeline.value[dataPointIndex];
          const color = p.days_left < 0 ? OVERDUE_COLOR : "#64748B";
          return `
            <div style="padding:8px 12px;font-size:12px">
              <div style="font-weight:600;margin-bottom:4px">${escapeHtml(p.name)}</div>
              <div>Due: ${formatDay(p.due_date)}</div>
              <div style="color:${color}">${describeDaysLeft(p.days_left)}</div>
            </div>`;
        },
      },
    }));

    onMounted(async () => {
      try {
        const response = await store.dispatch("fetchDueDatesOverview", {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
        if (response && response.ok) {
          buckets.value = response.data.buckets;
          projectCounts.value = response.data.projects;
          taskCounts.value = response.data.tasks;
          projectTimeline.value = response.data.projectTimeline || [];
        }
      } catch (error) {
        console.error("Error fetching due dates overview:", error);
      } finally {
        flagLoading.value = false;
      }
    });

    return {
      charts,
      isMobileView,
      flagLoading,
      projectTimeline,
      overdueCount,
      timelineSeries,
      timelineHeight,
      timelineOptions,
      OVERDUE_COLOR,
      DEFAULT_COLOR,
    };
  },
};
</script>
